import React, { useEffect, useRef, useMemo } from 'react';

const SnowfallCanvas = () => {
    // Constantes de configuration
    const NUM_FLAKES = 50; // Nombre initial de flocons

    // Utilisation de useMemo pour éviter de recalculer les valeurs constantes
    const FLAKE_SIZE = useMemo(() => ({ min: 8, max: 15 }), []);
    const FALL_SPEED = useMemo(() => ({ min: 0.1, max: 0.3 }), []);
    const WIND_SPEED = 0.3; // Constante, pas besoin de useMemo
    const CANVAS_OPACITY = 0.8; // Constante, pas besoin de useMemo
    const SNOWFLAKE_CHARS = useMemo(() => ['🎉', '✨', '🥂', '🎆', '🎊'], []);

    const canvasRef = useRef(null);
    const flakesRef = useRef([]);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Ajuste le canvas à la taille de la fenêtre
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        // Gestion du redimensionnement de la fenêtre
        window.addEventListener('resize', resizeCanvas);

        // Initialisation des flocons
        const initializeFlakes = () => {
            flakesRef.current = Array.from({ length: NUM_FLAKES }, () => ({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height,
                size: Math.random() * (FLAKE_SIZE.max - FLAKE_SIZE.min) + FLAKE_SIZE.min,
                speed: Math.random() * (FALL_SPEED.max - FALL_SPEED.min) + FALL_SPEED.min,
                wind: (Math.random() - 0.5) * WIND_SPEED,
                char: SNOWFLAKE_CHARS[Math.floor(Math.random() * SNOWFLAKE_CHARS.length)], // Flocon aléatoire
            }));
        };
        initializeFlakes();

        // Mise à jour et dessin des flocons
        const updateFlakes = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            flakesRef.current.forEach((flake, index) => {
                // Dessin du flocon
                ctx.font = `${flake.size}px Arial`;
                ctx.fillStyle = 'white';
                ctx.globalAlpha = CANVAS_OPACITY;
                ctx.fillText(flake.char, flake.x, flake.y);

                // Mise à jour des positions
                flake.y += flake.speed;
                flake.x += flake.wind;

                // Si un flocon quitte l'écran, il est réinitialisé
                if (flake.y > canvas.height || flake.x < -flake.size || flake.x > canvas.width + flake.size) {
                    flakesRef.current[index] = {
                        x: Math.random() * canvas.width,
                        y: -flake.size, // Repart du haut
                        size: Math.random() * (FLAKE_SIZE.max - FLAKE_SIZE.min) + FLAKE_SIZE.min,
                        speed: Math.random() * (FALL_SPEED.max - FALL_SPEED.min) + FALL_SPEED.min,
                        wind: (Math.random() - 0.5) * WIND_SPEED,
                        char: SNOWFLAKE_CHARS[Math.floor(Math.random() * SNOWFLAKE_CHARS.length)], // Nouveau flocon
                    };
                }
            });

            requestAnimationFrame(updateFlakes); // Boucle d'animation
        };

        updateFlakes();

        // Nettoyage
        return () => {
            window.removeEventListener('resize', resizeCanvas);
        };
    }, [NUM_FLAKES, FLAKE_SIZE, FALL_SPEED, WIND_SPEED, CANVAS_OPACITY, SNOWFLAKE_CHARS]); // Ajout des dépendances nécessaires

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 0, // En arrière-plan
            }}
        ></canvas>
    );
};

export default SnowfallCanvas;
